import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
    open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    const soleSubmenu = details === this.refs.details && !target ? this.#getSoleSubmenuDetails() : null;

    // Skip the root drawer's own opening transition when we're about to
    // immediately cascade into its one submenu, so the root doesn't visibly
    // fade/slide in at the same time as the submenu — it should just already
    // be "there" before the submenu animates in on top of it.
    if (soleSubmenu) {
      this.refs.menuDrawer.classList.add('menu-drawer--skip-transition');
    }

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });

      // Skip the intermediate single-item list when there's only one top-level link.
      if (soleSubmenu) {
        requestAnimationFrame(() => {
          this.refs.menuDrawer.classList.remove('menu-drawer--skip-transition');
          this.#autoOpenSoleSubmenu();
        });
      }
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    const details = this.#getDetailsElement(event);

    if (details === this.#getSoleSubmenuDetails()) {
      this.close();
      return;
    }

    this.#close(details);
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }

  /**
   * If the root menu has exactly one top-level link that opens its own
   * submenu (the "3 levels of links" pattern), return that link's details
   * element. Otherwise return null. Used to skip the pointless intermediate
   * single-item list when there's nothing else to choose from.
   * @returns {HTMLDetailsElement | null}
   */
  #getSoleSubmenuDetails() {
    const topLevelItems = this.refs.menuDrawer.querySelectorAll(':scope > nav > ul.menu-drawer__menu > li');

    if (topLevelItems.length !== 1) return null;

    const soleDetails = topLevelItems[0]?.querySelector(':scope > details');
    const summary = soleDetails?.querySelector(':scope > summary');

    if (!(soleDetails instanceof HTMLDetailsElement)) return null;
    if (summary?.getAttribute('on:click') !== 'header-drawer/open/submenu') return null;

    return soleDetails;
  }

  /**
   * Auto-opens the sole top-level submenu (see #getSoleSubmenuDetails) so
   * the drawer jumps straight to it instead of showing a single-item list.
   */
  #autoOpenSoleSubmenu() {
    const soleDetails = this.#getSoleSubmenuDetails();

    if (!soleDetails) return;

    const summary = soleDetails.querySelector(':scope > summary');

    summary?.setAttribute('aria-expanded', 'true');
    soleDetails.setAttribute('open', '');
    soleDetails.classList.add('menu-open');
    this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}